import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  X,
  User,
  Mail,
  Phone,
  Shield,
  Upload,
  Download,
  CheckCircle,
  Eye,
  EyeOff,
} from 'lucide-react';

const CreateUserModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'user',
    photo: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(f => ({ ...f, photo: file }));
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }

    setLoading(true);
    try {
      const body = new FormData();
      body.append('name', form.name);
      body.append('email', form.email);
      body.append('phone', form.phone);
      body.append('role', form.role);
      if (form.photo) body.append('photo', form.photo);

      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body,
        }
      );

      if (res.status === 401) {
        localStorage.removeItem('authToken');
        window.location.href = '/admin/login';
        return;
      }

      const data = await res.json();
      if (data.success) {
        setCreatedUser({ ...data.user, password: data.credentials.password });
        setStep('success');
        toast.success('User created successfully!');
      } else {
        toast.error(data.message || 'Failed to create user');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!createdUser) return;
    const csv = createdUser.credentialCsv ||
      `Name,Username,Email,Phone,Role,Password\n"${createdUser.name}","${createdUser.username}","${createdUser.email}","${createdUser.phone || ''}","${createdUser.role}","${createdUser.password}"`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credentials_${createdUser.username}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Credentials downloaded');
  };

  const inputStyle = {
    borderColor: '#BFC9D1',
    backgroundColor: '#EAEFEF',
    color: '#25343F',
  };

  const inputClass = "w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-all";

  const focusStyle = (e) => {
    e.target.style.borderColor = '#FF9B51';
    e.target.style.boxShadow = '0 0 0 3px rgba(255,155,81,0.12)';
  };
  const blurStyle = (e) => {
    e.target.style.borderColor = '#BFC9D1';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        className="bg-white rounded-2xl shadow-brand w-full max-w-md relative overflow-hidden"
        style={{ borderColor: '#BFC9D1', border: '1px solid #BFC9D1', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#EAEFEF' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#25343F' }}>
              {step === 'form' ? 'Create New User' : 'User Created!'}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: '#BFC9D1' }}>
              {step === 'form' ? 'Fill in the details below.' : 'Save or download credentials before closing.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            style={{ color: '#BFC9D1' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Form Step ── */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

            {/* Photo upload */}
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ backgroundColor: '#EAEFEF', border: '2px dashed #BFC9D1' }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-7 h-7" style={{ color: '#BFC9D1' }} />
                )}
              </div>
              <div>
                <label
                  htmlFor="photo-upload"
                  className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-colors hover:bg-gray-50"
                  style={{ borderColor: '#BFC9D1', color: '#25343F' }}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload Photo
                </label>
                <input id="photo-upload" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                <p className="text-xs mt-1" style={{ color: '#BFC9D1' }}>Optional. Max 5 MB.</p>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>Full Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                <input
                  type="text" name="name" value={form.name} onChange={handleChange} required
                  placeholder="e.g. Priya Sharma"
                  className={inputClass} style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>Email Address *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                <input
                  type="email" name="email" value={form.email} onChange={handleChange} required
                  placeholder="priya@example.com"
                  className={inputClass} style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                <input
                  type="tel" name="phone" value={form.phone} onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className={inputClass} style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#25343F' }}>Role *</label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#BFC9D1' }} />
                <select
                  name="role" value={form.role} onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none appearance-none"
                  style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {/* Info note */}
            <p className="text-xs rounded-xl px-3 py-2" style={{ backgroundColor: '#EAEFEF', color: '#25343F' }}>
              A random secure password will be auto-generated. You can download it after creation.
            </p>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button
                type="button" onClick={onClose}
                className="flex-1 py-3 rounded-xl border text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ borderColor: '#BFC9D1', color: '#25343F' }}
              >
                Cancel
              </button>
              <button
                type="submit" disabled={loading}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 shadow-accent"
                style={{ backgroundColor: '#FF9B51' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Creating…
                  </span>
                ) : 'Create User'}
              </button>
            </div>
          </form>
        )}

        {/* ── Success Step ── */}
        {step === 'success' && createdUser && (
          <div className="px-6 py-5 space-y-5">
            <div className="flex flex-col items-center text-center py-2">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(255,155,81,0.12)' }}>
                <CheckCircle className="w-8 h-8" style={{ color: '#FF9B51' }} />
              </div>
              <p className="text-base font-bold" style={{ color: '#25343F' }}>{createdUser.name}</p>
              <p className="text-xs mt-0.5" style={{ color: '#BFC9D1' }}>@{createdUser.username}</p>
            </div>

            {/* Credentials card */}
            <div className="rounded-2xl p-4 space-y-3" style={{ backgroundColor: '#EAEFEF', border: '1px solid #BFC9D1' }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#BFC9D1' }}>Login Credentials</p>
              {[
                { label: 'Username', value: createdUser.username },
                { label: 'Email', value: createdUser.email },
                { label: 'Role', value: createdUser.role },
                { label: 'Password', value: createdUser.password, secret: true },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: '#BFC9D1' }}>{item.label}</span>
                  {item.secret ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-semibold" style={{ color: '#25343F' }}>
                        {showPassword ? item.value : '••••••••••••'}
                      </span>
                      <button onClick={() => setShowPassword(v => !v)} style={{ color: '#BFC9D1' }}>
                        {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm font-semibold" style={{ color: '#25343F' }}>{item.value}</span>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-center" style={{ color: '#BFC9D1' }}>
              This password will not be shown again. Download the CSV to save it.
            </p>

            <div className="flex gap-3">
              <button
                onClick={downloadCSV}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-semibold transition-colors hover:bg-gray-50"
                style={{ borderColor: '#FF9B51', color: '#FF9B51' }}
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
              <button
                onClick={onCreated}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: '#25343F' }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateUserModal;
